import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FakturniaHighlightComponent } from '@fakturnia/components/highlight';
import { SnackbarService } from 'shared/services/snackbar.service';

export interface XmlPreviewDialogData {
  xml: string;
  title?: string;
  documentNumber?: string;
  ksefNumber?: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FakturniaHighlightComponent
  ],
  selector: 'app-xml-preview-dialog',
  templateUrl: './xml-preview-dialog.component.html',
  styleUrls: ['./xml-preview-dialog.component.scss']
})
export class XmlPreviewDialogComponent {

  xml: string = '';
  title: string = 'Podgląd XML';
  documentNumber: string = '';
  ksefNumber: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: XmlPreviewDialogData,
    private dialogRef: MatDialogRef<XmlPreviewDialogComponent>,
    private _snackbarService: SnackbarService
  ) {
    this.xml = data.xml || '';
    this.title = data.title || 'Podgląd XML';
    this.documentNumber = data.documentNumber || '';
    this.ksefNumber = data.ksefNumber || null;
  }

  /**
   * Getter dla sformatowanego XML z większymi wcięciami
   */
  get formattedXml(): string {
    if (!this.xml) return '';

    try {
      // Parsuj XML i sformatuj z 8 spacjami wcięcia
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(this.xml, 'text/xml');

      // Sprawdź błędy parsowania
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        return this.xml; // Zwróć oryginalny jeśli błąd parsowania
      }

      // Sformatuj z wcięciami
      return this.formatXml(xmlDoc);
    } catch (e) {
      console.error('Błąd formatowania XML:', e);
      return this.xml; // Fallback do oryginalnego
    }
  }

  /**
   * Formatuje XML z czytelnymi wcięciami (8 spacji - bardzo widoczne)
   */
  private formatXml(xml: Document | Element, level: number = 0): string {
    const indent = '        '; // 8 spacji - bardziej widoczne wcięcia
    const padding = indent.repeat(level);
    let formatted = '';

    if (xml instanceof Document) {
      // Deklaracja XML
      formatted += '<?xml version="1.0" encoding="UTF-8"?>\n';
      if (xml.documentElement) {
        formatted += this.formatXml(xml.documentElement, level);
      }
      return formatted;
    }

    const element = xml as Element;
    const tagName = element.tagName;
    const attributes = element.attributes;
    const children = element.childNodes;

    // Otwórz tag
    formatted += padding + '<' + tagName;

    // Atrybuty
    for (let i = 0; i < attributes.length; i++) {
      const attr = attributes[i];
      formatted += ' ' + attr.name + '="' + attr.value + '"';
    }

    // Sprawdź czy ma dzieci
    const hasElementChildren = Array.from(children).some(child => child.nodeType === 1);
    const textContent = element.textContent?.trim() || '';

    if (children.length === 0) {
      // Samozamykający tag
      formatted += '/>\n';
    } else if (!hasElementChildren && textContent) {
      // Tylko tekst
      formatted += '>' + textContent + '</' + tagName + '>\n';
    } else {
      // Ma dzieci
      formatted += '>\n';

      // Dzieci
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child.nodeType === 1) { // Element node
          formatted += this.formatXml(child as Element, level + 1);
        }
      }

      // Zamknij tag
      formatted += padding + '</' + tagName + '>\n';
    }

    return formatted;
  }

  /**
   * Rozmiar XML w KB
   */
  get xmlSizeKb(): string {
    return (this.xml.length / 1024).toFixed(2);
  }

  /**
   * Pobiera XML jako plik
   */
  downloadXml(): void {
    const blob = new Blob([this.xml], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const filename = this.documentNumber 
      ? `${this.documentNumber.replace(/\//g, '_')}.xml`
      : `${this.ksefNumber || 'faktura'}.xml`;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    
    this._snackbarService.showSnackBar('Pobrano plik XML', 'success', 3000);
  }

  /**
   * Kopiuje XML do schowka
   */
  copyToClipboard(): void {
    navigator.clipboard.writeText(this.xml).then(() => {
      this._snackbarService.showSnackBar('Skopiowano XML do schowka', 'success', 3000);
    }).catch(() => {
      this._snackbarService.showSnackBar('Nie udało się skopiować', 'error', 3000);
    });
  }

  /**
   * Zamyka dialog
   */
  close(): void {
    this.dialogRef.close();
  }
}
